/** All audio in this project is either procedurally synthesized here or, for future
 * songs, loaded from real audio files by AudioManager. Nothing here touches any
 * copyrighted or third-party sound material. */

/** Converts a MIDI note number (69 = A4 = 440Hz, 60 = middle C) into a frequency in Hz. */
export function noteToFrequency(midiNote: number): number {
  return 440 * Math.pow(2, (midiNote - 69) / 12);
}

function applyEnvelope(
  gain: GainNode,
  ctx: BaseAudioContext,
  startTime: number,
  peak: number,
  attack: number,
  decay: number,
  sustain: number,
  releaseStart: number,
  release: number
) {
  const g = gain.gain;
  g.cancelScheduledValues(startTime);
  g.setValueAtTime(0, startTime);
  g.linearRampToValueAtTime(peak, startTime + attack);
  g.linearRampToValueAtTime(peak * sustain, startTime + attack + decay);
  g.setValueAtTime(peak * sustain, releaseStart);
  g.linearRampToValueAtTime(0.0001, releaseStart + release);
}

/**
 * Synthesizes a short, cheerful "meow"-like vocal blip in real time on a live
 * AudioContext. This is the placeholder for the cat vocal system; AudioManager
 * prefers a real recorded sample when one is registered for a cat, and falls
 * back to this synth otherwise, so swapping in real vocals later requires no
 * gameplay code changes.
 */
export function synthesizeMeow(
  ctx: BaseAudioContext,
  destination: AudioNode,
  pitch: number,
  when: number,
  velocity = 1
): void {
  const baseFreq = noteToFrequency(pitch);
  const duration = 0.32;

  const voice = ctx.createGain();
  voice.gain.value = 0.0001;
  voice.connect(destination);

  // Two detuned oscillators glide upward then settle, mimicking a bright "mew".
  [1, 1.008].forEach((detune, i) => {
    const osc = ctx.createOscillator();
    osc.type = i === 0 ? 'sawtooth' : 'triangle';
    osc.frequency.setValueAtTime(baseFreq * 0.72, when);
    osc.frequency.exponentialRampToValueAtTime(baseFreq * detune, when + 0.09);
    osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.94 * detune, when + duration * 0.9);

    const oscGain = ctx.createGain();
    oscGain.gain.value = i === 0 ? 0.55 : 0.35;
    osc.connect(oscGain);
    oscGain.connect(voice);
    osc.start(when);
    osc.stop(when + duration + 0.05);
  });

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(baseFreq * 2.2, when);
  filter.frequency.exponentialRampToValueAtTime(baseFreq * 5, when + 0.1);
  filter.Q.value = 4;
  voice.disconnect();
  voice.connect(filter);
  filter.connect(destination);

  applyEnvelope(voice, ctx, when, 0.5 * velocity, 0.02, 0.08, 0.55, when + duration * 0.55, duration * 0.5);
}

/** A comedic, splattery "you missed the treat!" sound for level failure -- a wet
 * noise splash over a low descending thud, meant to land as a clear,
 * attention-grabbing "that's game over" cue (any miss ends the level). */
export function synthesizeSplat(ctx: BaseAudioContext, destination: AudioNode, when: number): void {
  const thud = ctx.createOscillator();
  thud.type = 'sine';
  const thudGain = ctx.createGain();
  thud.frequency.setValueAtTime(180, when);
  thud.frequency.exponentialRampToValueAtTime(45, when + 0.28);
  applyEnvelope(thudGain, ctx, when, 0.8, 0.005, 0.08, 0.3, when + 0.1, 0.28);
  thud.connect(thudGain);
  thudGain.connect(destination);
  thud.start(when);
  thud.stop(when + 0.4);

  const noise = ctx.createBufferSource();
  noise.buffer = whiteNoiseBuffer(ctx, 0.3);
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1400, when);
  filter.frequency.exponentialRampToValueAtTime(250, when + 0.25);
  const noiseGain = ctx.createGain();
  applyEnvelope(noiseGain, ctx, when, 0.6, 0.002, 0.05, 0.2, when + 0.08, 0.2);
  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(destination);
  noise.start(when);
  noise.stop(when + 0.35);
}

/** A short, bright three-note ascending bell arpeggio for spending coins to unlock a
 * cat or background -- a cheerful "cha-ching" cue timed to line up with the coin
 * counter animation in the shop scenes. */
export function synthesizeUnlock(ctx: BaseAudioContext, destination: AudioNode, when: number): void {
  const notes = [523.25, 659.25, 783.99]; // C5, E5, G5 -- a bright major triad walk-up
  notes.forEach((freq, i) => {
    const start = when + i * 0.09;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    applyEnvelope(gain, ctx, start, 0.32, 0.005, 0.05, 0.4, start + 0.08, 0.22);
    osc.connect(gain);
    gain.connect(destination);
    osc.start(start);
    osc.stop(start + 0.32);

    // A quiet octave-up shimmer layered on top gives the chime a "sparkle" rather
    // than a plain tone.
    const shimmer = ctx.createOscillator();
    shimmer.type = 'sine';
    shimmer.frequency.value = freq * 2;
    const shimmerGain = ctx.createGain();
    applyEnvelope(shimmerGain, ctx, start, 0.1, 0.005, 0.04, 0.3, start + 0.06, 0.18);
    shimmer.connect(shimmerGain);
    shimmerGain.connect(destination);
    shimmer.start(start);
    shimmer.stop(start + 0.26);
  });
}

/** A bigger, more triumphant fanfare than synthesizeUnlock -- for the one-time coin
 * celebration on a completed level's Results screen, which should feel like a
 * standout reward moment rather than a routine shop purchase chime. A five-note
 * ascending run (rather than three) that overshoots up to a bright octave and holds
 * there with a wide shimmer, timed to land under the big coin-count-up animation. */
export function synthesizeCoinCelebration(ctx: BaseAudioContext, destination: AudioNode, when: number): void {
  const notes = [523.25, 659.25, 783.99, 987.77, 1046.5]; // C5, E5, G5, B5, C6 -- rises past the octave before settling
  notes.forEach((freq, i) => {
    const start = when + i * 0.075;
    const isLast = i === notes.length - 1;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    const duration = isLast ? 0.55 : 0.26;
    applyEnvelope(gain, ctx, start, isLast ? 0.42 : 0.34, 0.005, 0.05, isLast ? 0.55 : 0.4, start + (isLast ? 0.16 : 0.08), duration * 0.75);
    osc.connect(gain);
    gain.connect(destination);
    osc.start(start);
    osc.stop(start + duration + 0.05);

    const shimmer = ctx.createOscillator();
    shimmer.type = 'sine';
    shimmer.frequency.value = freq * 2;
    const shimmerGain = ctx.createGain();
    applyEnvelope(shimmerGain, ctx, start, isLast ? 0.16 : 0.1, 0.005, 0.04, 0.35, start + (isLast ? 0.14 : 0.06), duration * 0.7);
    shimmer.connect(shimmerGain);
    shimmerGain.connect(destination);
    shimmer.start(start);
    shimmer.stop(start + duration);
  });

  // A soft high sparkle swell under the final held note, so the landing note reads
  // as a little "ta-da" rather than just the loudest note in the run.
  const sparkleStart = when + notes.length * 0.075;
  const sparkle = ctx.createBufferSource();
  sparkle.buffer = whiteNoiseBuffer(ctx, 0.4);
  const sparkleFilter = ctx.createBiquadFilter();
  sparkleFilter.type = 'highpass';
  sparkleFilter.frequency.value = 6000;
  const sparkleGain = ctx.createGain();
  applyEnvelope(sparkleGain, ctx, sparkleStart, 0.12, 0.02, 0.1, 0.3, sparkleStart + 0.15, 0.25);
  sparkle.connect(sparkleFilter);
  sparkleFilter.connect(sparkleGain);
  sparkleGain.connect(destination);
  sparkle.start(sparkleStart);
  sparkle.stop(sparkleStart + 0.4);
}

function whiteNoiseBuffer(ctx: BaseAudioContext, duration: number): AudioBuffer {
  const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * duration), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

function scheduleKick(ctx: BaseAudioContext, dest: AudioNode, when: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(150, when);
  osc.frequency.exponentialRampToValueAtTime(45, when + 0.14);
  applyEnvelope(gain, ctx, when, 0.9, 0.001, 0.05, 0.3, when + 0.05, 0.14);
  osc.connect(gain);
  gain.connect(dest);
  osc.start(when);
  osc.stop(when + 0.2);
}

function scheduleHat(ctx: BaseAudioContext, dest: AudioNode, when: number, open = false) {
  const noise = ctx.createBufferSource();
  noise.buffer = whiteNoiseBuffer(ctx, 0.1);
  const filter = ctx.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = 7000;
  const gain = ctx.createGain();
  const dur = open ? 0.16 : 0.045;
  applyEnvelope(gain, ctx, when, 0.18, 0.001, dur * 0.3, 0.2, when + dur * 0.3, dur * 0.6);
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(dest);
  noise.start(when);
  noise.stop(when + dur + 0.05);
}

function scheduleBass(ctx: BaseAudioContext, dest: AudioNode, when: number, freq: number, dur: number) {
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = freq;
  const gain = ctx.createGain();
  applyEnvelope(gain, ctx, when, 0.45, 0.005, dur * 0.2, 0.6, when + dur * 0.6, dur * 0.35);
  osc.connect(gain);
  gain.connect(dest);
  osc.start(when);
  osc.stop(when + dur + 0.05);
}

function schedulePluck(ctx: BaseAudioContext, dest: AudioNode, when: number, freq: number, dur: number) {
  const osc = ctx.createOscillator();
  osc.type = 'square';
  osc.frequency.value = freq;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = freq * 4;
  const gain = ctx.createGain();
  applyEnvelope(gain, ctx, when, 0.22, 0.002, dur * 0.3, 0.15, when + dur * 0.35, dur * 0.4);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(dest);
  osc.start(when);
  osc.stop(when + dur + 0.05);
}

/** A cheerful, fully original chiptune-style loop chord progression (Am - F - C - G, one bar each). */
const CHORD_PROGRESSION: number[][] = [
  [220.0, 261.63, 329.63], // Am
  [174.61, 220.0, 261.63], // F
  [130.81, 164.81, 196.0], // C
  [196.0, 246.94, 293.66], // G
];

const ARP_SCALE_OFFSETS = [0, 3, 5, 7, 10, 12, 10, 7]; // pentatonic-ish walk within a chord

/**
 * Renders a full song track offline (fast, deterministic) so playback is a simple
 * buffer -- no live scheduling jitter. `kind` selects between a plain metronome
 * click track (useful for calibration/testing) and a cheerful original pop loop.
 */
export async function renderSynthTrack(
  kind: 'metronome' | 'popLoop',
  bpm: number,
  duration: number
): Promise<AudioBuffer> {
  const sampleRate = 44100;
  const offline = new OfflineAudioContext(2, Math.ceil(sampleRate * duration), sampleRate);
  const secPerBeat = 60 / bpm;
  const master = offline.createGain();
  master.gain.value = 0.9;
  master.connect(offline.destination);

  if (kind === 'metronome') {
    let beatIndex = 0;
    for (let t = 0; t < duration; t += secPerBeat) {
      const accent = beatIndex % 4 === 0;
      const osc = offline.createOscillator();
      const gain = offline.createGain();
      osc.type = 'sine';
      osc.frequency.value = accent ? 1400 : 900;
      applyEnvelope(gain, offline, t, accent ? 0.7 : 0.45, 0.001, 0.03, 0.1, t + 0.03, 0.05);
      osc.connect(gain);
      gain.connect(master);
      osc.start(t);
      osc.stop(t + 0.08);
      beatIndex++;
    }
  } else {
    const secPer16th = secPerBeat / 4;
    let barIndex = 0;
    let sixteenthIndex = 0;
    for (let t = 0; t < duration; t += secPer16th) {
      const beatInBar = Math.floor(sixteenthIndex / 4) % 4;
      const stepInBeat = sixteenthIndex % 4;
      const chord = CHORD_PROGRESSION[barIndex % CHORD_PROGRESSION.length];

      // Kick on beats 1 and 3.
      if (stepInBeat === 0 && (beatInBar === 0 || beatInBar === 2)) {
        scheduleKick(offline, master, t);
      }
      // Hats on every 8th note, open hat on the "and" of beat 4.
      if (stepInBeat === 0 || stepInBeat === 2) {
        scheduleHat(offline, master, t, beatInBar === 3 && stepInBeat === 2);
      }
      // Bass on beat 1 and 3 of each bar, root of the current chord.
      if (stepInBeat === 0 && (beatInBar === 0 || beatInBar === 2)) {
        scheduleBass(offline, master, t, chord[0] / 2, secPerBeat * 1.9);
      }
      // Arpeggio pluck walking the chord tones every 16th note.
      const arpDegree = ARP_SCALE_OFFSETS[sixteenthIndex % ARP_SCALE_OFFSETS.length];
      const chordTone = chord[arpDegree % chord.length];
      const freq = chordTone * Math.pow(2, Math.floor(arpDegree / chord.length));
      schedulePluck(offline, master, t, freq, secPer16th * 0.9);

      sixteenthIndex++;
      if (sixteenthIndex % 16 === 0) barIndex++;
    }
  }

  return offline.startRendering();
}
